import { type ReactNode } from 'react';
import { create } from 'zustand';
import { ulid } from 'ulid';
import { type MetadataCsv } from '@/hooks/data/inspector/useUploadStore';
import { type EditCreateSubmissionLinkModalProps } from '@/types/link';
import { type WorkflowStatusModalProps } from '@/types/workflow';
import { type EditCreateShareModalProps } from '@/types/share';
import { type DAMNodeType, type NodeType } from '@/types/tree';
import { type ButtonProps } from '@/components/ui/button';
import { type VariantLimits } from '@/types/variant';
import { type Asset } from '@/types/asset';

export enum ModalId {
  AssetNote = 'assetNote',
  Confirm = 'confirm',
  CreateAlbum = 'createAlbum',
  CreateAssetNotes = 'createAssetNotes',
  CreateDestination = 'createDestination',
  CreateFolder = 'createFolder',
  CreateMetadata = 'createMetadata',
  CreateTag = 'createTag',
  CreateUser = 'createUser',
  CreateVariant = 'createVariant',
  CreateWorkflow = 'createWorkflow',
  CreateWorkflowStatus = 'createWorkflowStatus',
  CreateWorkflowStatusInspector = 'createWorkflowStatusInspector',
  Csv = 'csv',
  DeleteConfirmation = 'deleteConfirmation',
  DeleteConfirmationAlertDialog = 'deleteConfirmationAlertDialog',
  EditAlbum = 'editAlbum',
  EditCreateShare = 'editCreateShare',
  EditCreateSubmissionLink = 'editCreateSubmissionLink',
  EditFolder = 'editFolder',
  EditShare = 'editShare',
  IngestionAlbumSelect = 'ingestionAlbumSelect',
  IngestionFolderSelect = 'ingestionFolderSelect',
  KeyboardShortcuts = 'keyboardShortcuts',
  RestoreTrashConfirmation = 'restoreTrashConfirmation',
  UploadAssetVersion = 'uploadAssetVersion',
  VersionPreview = 'versionPreview',
  DownloadZip = 'downloadZip',
  ShareDownload = 'shareDownload',
}

type OptionalModalProps = undefined;

export type ModalRegistry = {
  [ModalId.AssetNote]: { note: string };
  [ModalId.Confirm]: {
    content: ReactNode;
    onConfirm: () => void | Promise<void>;
    dismissButtonLabel?: string;
    confirmButtonLabel?: string;
    confirmButtonDataCy?: string;
    dismissButtonDataCy?: string;
    confirmButtonVariant?: ButtonProps['variant'];
    dismissButtonVariant?: ButtonProps['variant'];
  };
  [ModalId.CreateAlbum]: { id?: string; mode?: 'ingestion' };
  [ModalId.CreateAssetNotes]: OptionalModalProps;
  [ModalId.CreateDestination]: OptionalModalProps;
  [ModalId.CreateFolder]: { id?: string; mode?: 'ingestion' } | undefined;
  [ModalId.CreateMetadata]: OptionalModalProps;
  [ModalId.CreateTag]: OptionalModalProps;
  [ModalId.CreateUser]: OptionalModalProps;
  [ModalId.CreateVariant]: { variantLimits: VariantLimits };
  [ModalId.CreateWorkflow]: OptionalModalProps;
  [ModalId.CreateWorkflowStatus]: WorkflowStatusModalProps;
  [ModalId.CreateWorkflowStatusInspector]: WorkflowStatusModalProps;
  [ModalId.Csv]: {
    text: ReactNode;
    keep: () => void;
    overwrite: () => void;
    originalCsvData: Record<string, MetadataCsv>;
  };
  [ModalId.DeleteConfirmation]: {
    id?: string | number;
    name?: string;
    count?: number;
    data?: { type?: NodeType | DAMNodeType | string; callBack?: () => void };
  };
  [ModalId.DeleteConfirmationAlertDialog]: {
    'content': ReactNode;
    'onClick'?: () => void;
    'confirmLabel'?: string;
    'data-cy': string;
  };
  [ModalId.EditAlbum]: { id: string };
  [ModalId.EditCreateShare]: EditCreateShareModalProps;
  [ModalId.EditCreateSubmissionLink]: EditCreateSubmissionLinkModalProps;
  [ModalId.EditFolder]: { id: string };
  [ModalId.EditShare]: { nodeId: string; shareId: string };
  [ModalId.IngestionAlbumSelect]: OptionalModalProps;
  [ModalId.IngestionFolderSelect]: OptionalModalProps;
  [ModalId.KeyboardShortcuts]: OptionalModalProps;
  [ModalId.RestoreTrashConfirmation]: OptionalModalProps;
  [ModalId.UploadAssetVersion]: { id: string };
  [ModalId.VersionPreview]: {
    assetVersion: {
      originalFileName: string;
      thumbnail?: string | null;
      preview?: string | null;
      fileType: string;
    };
  };
  [ModalId.DownloadZip]: {
    nodeIds: Array<string>;
    sourceType: DAMNodeType | NodeType.Assets;
    name?: string;
    assets?: Array<Asset>;
  };
  [ModalId.ShareDownload]: {
    shareId: string;
    nodeIds: Array<string>;
    downloadableVariants: Array<{ id: string; name: string; slug: string }>;
    name?: string;
    assets?: Array<Asset>;
    includeOriginalFile?: boolean;
  };
};

export type ModalType = keyof ModalRegistry;

// Make `id` and `props` narrow together when switching on entry.id.
export type ModalEntry<K extends ModalType = ModalType> = K extends ModalType
  ? {
      key: string;
      id: K;
      props: ModalRegistry[K];
      // Closing entries stay mounted until Radix emits the content exit animation event.
      isClosing?: boolean;
    }
  : never;

type ModalArgs<K extends ModalType> = undefined extends ModalRegistry[K]
  ? [props?: ModalRegistry[K]]
  : [props: ModalRegistry[K]];

type ModalStore = {
  stack: Array<ModalEntry>;
  open: <K extends ModalType>(type: K, ...args: ModalArgs<K>) => string;
  replaceTop: <K extends ModalType>(type: K, ...args: ModalArgs<K>) => void;
  back: () => void;
  closeAll: () => void;
  close: (type?: ModalType) => void;
  removeClosed: (key: string) => void;
};

const createEntry = <K extends ModalType>(id: K, props: ModalRegistry[K]): ModalEntry<K> =>
  ({
    key: ulid(),
    id,
    props,
  }) as ModalEntry<K>;

const getCurrentModal = (stack: Array<ModalEntry>) => stack.at(-1);

export const useModalStore = create<ModalStore>()((set, get) => ({
  stack: [],
  open: (type, ...args) => {
    const [props] = args;

    const entry = createEntry(type, props as ModalRegistry[typeof type]);

    set((state) => ({ stack: [...state.stack, entry] }));

    return entry.key;
  },
  replaceTop: (type, ...args) => {
    const [props] = args;

    const entry = createEntry(type, props as ModalRegistry[typeof type]);

    set((state) => ({
      stack: state.stack.length ? [...state.stack.slice(0, -1), entry] : [entry],
    }));
  },
  back: () =>
    set((state) => {
      const currentEntry = getCurrentModal(state.stack);

      if (!currentEntry) {
        return state;
      }

      return {
        stack: state.stack.map((entry) => (entry.key === currentEntry.key ? { ...entry, isClosing: true } : entry)),
      };
    }),
  // Covered entries render with open={false}, so they never transition and never reach
  // ModalContent's onAnimationEnd — flagging them would strand them in the stack forever.
  // They are invisible, so drop them outright and let only the visible top entry animate out.
  closeAll: () =>
    set((state) => {
      const currentEntry = getCurrentModal(state.stack);

      if (!currentEntry) {
        return state;
      }

      return { stack: [{ ...currentEntry, isClosing: true }] };
    }),
  // Dismissing the top entry is exactly `back`; `close` only adds the guard that an id, when
  // given, must match the top entry. Both flag a single entry and never the whole stack:
  // `removeClosed` is driven solely by ModalContent's onAnimationEnd, and a covered entry
  // renders with open={false} so it never animates — flagging it would strand it in the stack
  // forever, invisible but still mounted and still counted by useModalStackLength. Use
  // closeAll to dismiss the whole stack.
  close: (id) => {
    const currentEntry = getCurrentModal(get().stack);

    if (!currentEntry || (id && currentEntry.id !== id)) {
      return;
    }

    get().back();
  },
  // Remove by stack key instead of modal id so each opened instance has its own lifecycle.
  removeClosed: (key) =>
    set((state) => ({
      stack: state.stack.filter((entry) => entry.key !== key),
    })),
}));

export const useModalStack = () => useModalStore((state) => state.stack);

export const useModalStackLength = () => useModalStore((state) => state.stack.length);

export const useOpenModal = () => useModalStore((state) => state.open);

export const useReplaceTopModal = () => useModalStore((state) => state.replaceTop);

export const useBackModal = () => useModalStore((state) => state.back);

export const useCloseAllModals = () => useModalStore((state) => state.closeAll);

export const useCloseModal = () => useModalStore((state) => state.close);

export const useRemoveClosedModal = () => useModalStore((state) => state.removeClosed);

// Returns the id of the top modal in the stack.
export const useCurrentModalId = () => useModalStore((state) => getCurrentModal(state.stack)?.id);

// Convenience selector for components that only need to know whether one modal is active.
export const useIsCurrentModal = (id: ModalType) =>
  useModalStore((state) => {
    const entry = getCurrentModal(state.stack);

    return entry?.id === id && !entry.isClosing;
  });

export const useModalEntryKey = (id: ModalType) =>
  useModalStore((state) => {
    for (let index = state.stack.length - 1; index >= 0; index -= 1) {
      const entry = state.stack[index];

      if (entry.id === id) {
        return entry.key;
      }
    }

    return undefined;
  });

export const useIsModalClosing = (id: ModalType) =>
  useModalStore((state) => {
    for (let index = state.stack.length - 1; index >= 0; index -= 1) {
      const entry = state.stack[index];

      if (entry.id === id) {
        return entry.isClosing === true;
      }
    }

    return false;
  });

// Props are only exposed for the current modal to avoid reading stale data from hidden stack entries.
export const useCurrentModalProps = <K extends ModalType>(id: K): ModalRegistry[K] | undefined =>
  useModalStore((state) => {
    const entry = getCurrentModal(state.stack);

    if (entry?.id !== id) {
      return undefined;
    }

    return entry.props as ModalRegistry[K];
  });
